/**
 * Call events are ephemeral: nothing replays the ones a sleeping tab or a
 * dropped line missed (calls-v1 §7.10). So whoever holds call state reads it
 * again when the tab is looked at, and when the connection comes back.
 */

import { useEffect, useRef } from 'react'

import { useConnectionState } from '@/api/store'

export function useCallResync(onResync: () => void): void {
  const callback = useRef(onResync)
  callback.current = onResync

  const { status } = useConnectionState()
  const previous = useRef(status)

  // Back online after being anything else. Being online at mount is not a
  // return, and asking then would only repeat the first load.
  useEffect(() => {
    const wasOnline = previous.current === 'online'
    previous.current = status
    if (status === 'online' && !wasOnline) callback.current()
  }, [status])

  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') callback.current()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])
}
